import { DZ_COLOR_BLK_BLACK, DZ_COLOR_BLK_PURPLE, DZ_COLOR_NEUTRAL_300 } from '@blk/daisy/core/styles';
import { CURRENCY_SYMBOLS } from '@ddt-web/common';
import { TabData } from '@ddt-web/data-service';
import { ObjectUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';

import { ChartFactory } from '../factories';
import { TooltipDispatcher } from '../tooltips/dispatcher.tooltip';
import { ChartConfigHelper } from './config.helper';
import { DefaultChartConfig } from './default-chart.config';
import { ChartType } from './enums/chart-type.enum';
import { LineChartName } from './enums/line-chart-name.enum';

declare const Highcharts;

const DEFAULT_LINE_CHART_CONFIG: any = {
	legend: {
		enabled: true,
		align: 'bottom',
		layout: 'vertical',
		verticalAlign: 'bottom',
		itemMarginBottom: 10
	}
};

export class LineChartConfig extends DefaultChartConfig {
	private seriesColors: Array<string> = [];
	private seriesData: Array<any> = [];
	private multiplier: number = 10000;
	private displayCurrency: string = CURRENCY_SYMBOLS.USD;

	constructor(
		protected type: ChartType,
		protected name: string,
		protected container: HTMLElement,
		protected data: TabData | Array<TabData>,
		protected dataIndex?: number,
		protected colors?: Array<string>,
		protected height?: number,
		protected width?: number,
		protected title?: string,
		protected hideTitle?: boolean,
		protected x?: string,
		protected y?: string,
		protected customAttributes?: any,
		protected tooltipType?: string
	) {
		super(type, container, colors, height, width, title, hideTitle);
		this.initialize();
	}

	private initialize(): void {
		const data = [].concat(this.data);
		this.displayCurrency = this.customAttributes?.displayCurrency;
		this.configuration = ObjectUtil.extend(this.configuration, DEFAULT_LINE_CHART_CONFIG);
		this.multiplier = this.customAttributes?.growthMultiplier ?? 10000;
		this.seriesColors = this.configuration.colors || ChartFactory.getPortfolioColors();

		if (this.name === LineChartName.NilePerformance || this.name === LineChartName.IqAnalyticsChart) {
			this.seriesData = ChartFactory.getRollingLineSeriesForTabData(data[0], this.multiplier);
		} else {
			this.seriesData = ChartFactory.getDefaultLineSeriesForTabDataPas(data, this.multiplier, this.customAttributes['multiSeriesConfig']);
		}

		this.updateDefaultConfiguration();
		if (this.name === LineChartName.NilePerformance) {
			this.updateRollingPerformanceConfiguration();
		}

		if (this.name === LineChartName.IqAnalyticsChart) {
			this.updateIqRollingPerformanceConfiguration();
		}

		const { growthMultiplier, displayCurrency, tooltipPrefixes, multiSeriesConfig, ...validHighChartAttributes } = this.customAttributes;
		this.configuration = ChartConfigHelper.applyCustomAttributesToConfiguration(validHighChartAttributes, this.configuration);
	}

	private updateDefaultConfiguration(): void {
		const currency = this.displayCurrency;
		this.configuration = ObjectUtil.extend(this.configuration, {
			colors: this.seriesColors,
			series: this.seriesData,
			chart: {
				marginTop: 10,
				marginBottom: 60 + 24 * this.seriesData.length
			},
			tooltip: {
				shared: true,
				backgroundColor: 'rgba(255, 255, 255, 0.9)',
				borderWidth: 1,
				useHTML: true,
				formatter: new TooltipDispatcher(this.type, this.tooltipType).getTooltipFormatter({
					multiplier: this.multiplier,
					displayCurrency: this.displayCurrency,
					customData: this.customAttributes?.customData
				})
			},
			xAxis: {
				changed: false,
				type: 'datetime',
				labels: {
					formatter: function (): void {
						return Highcharts.dateFormat('%m/%d/%Y', this.value);
					},
					staggerLines: 2,
					style: {
						color: 'grey'
					}
				},
				minRange: 30 * 24 * 3600 * 1000,
				minTickInterval: 30 * 24 * 3600 * 1000
			},
			yAxis: {
				labels: {
					formatter: function (): string {
						return currency + Highcharts.numberFormat(Math.abs(this.value), 0, '.', ',');
					},
					align: 'right',
					style: {
						color: 'grey'
					}
				},
				opposite: false,
				title: {
					enabled: false
				}
			},
			plotOptions: {
				series: {
					lineWidth: 3
				}
			}
		});
	}

	private updateRollingPerformanceConfiguration(): void {
		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: { marginTop: 10, spacingBottom: 10 },
			legend: {
				align: 'left',
				verticalAlign: 'top',
				itemStyle: {
					fontFamily: 'FortBook, Arial, sans-serif',
					fontSize: '14px',
					fontWeight: 'normal'
				}
			},
			xAxis: {
				tickLength: 10,
				tickWidth: 1,
				enabled: true,
				tickColor: '#a5a5a5',
				lineColor: '#a5a5a5',
				labels: {
					step: 2,
					style: { fontSize: '14px', fontFamily: 'FortBook, Arial, sans-serif', color: 'grey' }
				}
			},
			yAxis: {
				gridLineColor: '#a5a5a5',
				title: {
					enabled: true,
					style: { fontSize: '14px', fontFamily: 'FortBook, Arial, sans-serif', color: 'grey' }
				},
				labels: {
					enabled: true,
					style: { fontSize: '14px', fontFamily: 'FortBook, Arial, sans-serif', color: 'grey' }
				},
				plotLines: [
					{
						color: '#000',
						value: 0,
						width: 1,
						zIndex: 3
					}
				]
			}
		});
	}

	private updateIqRollingPerformanceConfiguration(): void {
		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: {
				marginTop: 20,
				spacingBottom: 10,
				height: 280
			},
			plotOptions: {
				column: {
					stacking: null,
					pointWidth: 24
				}
			},
			colors: [DZ_COLOR_BLK_PURPLE],
			yAxis: {
				plotLines: [
					{
						value: 0,
						color: DZ_COLOR_BLK_BLACK,
						zIndex: 5
					}
				],
				title: {
					enabled: true,
					text: get(this.data, 'tableData[0].firstColumnTitle.formattedValue', ''),
					style: { fontSize: '14px', fontFamily: '"FortBook", Arial, sans-serif', color: DZ_COLOR_BLK_BLACK },
					margin: 24
				},
				labels: {
					format: '{value:.1f}',
					formatter: null,
					style: {
						fontFamily: '"FortBook", Arial, sans-serif',
						fontSize: '12px',
						color: DZ_COLOR_NEUTRAL_300
					}
				}
			},
			xAxis: {
				opposite: false,
				labels: {
					enabled: true,
					formatter: function (): void {
						return Highcharts.dateFormat('%m/%Y', this.value);
					},
					staggerLines: 0,
					style: {
						fontFamily: '"FortBook", Arial, sans-serif',
						fontSize: '12px',
						color: DZ_COLOR_NEUTRAL_300,
						lineHeight: '16px',
						textOverflow: 'none',
						whiteSpace: 'nowrap'
					},
					padding: 100
				},
				tickLength: 10,
				tickWidth: 1,
				tickmarkPlacement: 'on',
				tickColor: DZ_COLOR_NEUTRAL_300
			},
			tooltip: {
				enabled: true
			},
			legend: {
				enabled: false
			}
		});
	}

	public getConfigurationForPDF(customAttributes: any = {}): any {
		let config = this.updateDefaultConfigurationForPDF();
		if (this.name === LineChartName.NilePerformance) {
			config = this.updateRollingPerformanceConfigurationForPDF(config);
		}

		return ChartConfigHelper.applyCustomAttributesToConfiguration(customAttributes, config);
	}

	public updateRollingPerformanceConfigurationForPDF(config: any): any {
		return ObjectUtil.extend(config, {
			yAxis: {
				title: {
					style: { fontSize: '8px' }
				}
			},
			xAxis: {
				tickAmount: 5
			}
		});
	}

	public updateDefaultConfigurationForPDF(): any {
		return ObjectUtil.extend(this.configuration, {
			chart: {
				marginTop: 2,
				marginBottom: 70
			},
			legend: {
				itemStyle: { fontSize: '8px', fontWeight: 'normal' },
				itemMarginBottom: 4,
				x: -7,
				y: 8
			},
			xAxis: {
				lineWidth: 0.25,
				lineColor: '#000',
				labels: {
					style: { fontSize: '8px' }
				},
				tickWidth: 0.25,
				tickColor: '#000'
			},
			yAxis: {
				gridLineWidth: 0.25,
				gridLineColor: '#616161',
				labels: {
					style: { fontSize: '8px' }
				},
				plotLines: [
					{
						width: 0.3
					}
				]
			}
		});
	}

	public render(): void {
		Highcharts.chart(this.configuration);
	}
}
